import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import createStyles from "@material-ui/core/styles/createStyles";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { SvgIconProps } from "@material-ui/core/SvgIcon";
import ExpandLess from "@material-ui/icons/ExpandLessRounded";
import ExpandMore from "@material-ui/icons/ExpandMoreRounded";
import clsx from "clsx";
import React, { ComponentPropsWithRef } from "react";
import { Color } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { NavigationListItem, NavigationListItemContent } from "./NavigationCommonElement";

export interface NavigationMenuElementBaseProps extends HasTestId {
    /**
     * Shows a badge in different variants according to the value below. Defaults to "no badge" if `undefined`.
     *
     * - `false` -> no badge
     * - `true` -> simple "dot" badge
     * - `number > 0` -> "standard" badge with "99+" value if number greater or equal to `100`
     */
    badgeCounter?: number | boolean;
    /**
     * Mandatory label / text of the menu element.
     */
    label: NonNullable<React.ReactNode>;
    /**
     * General purpose of this `onClick` property is for handling `onBurgerMenuClose` in `burgerableMenu` because
     * e.g. if the burgerable menu button stays because of routing to an internal link the burgeralbe menu should be closed.
     * Defaults to "do nothing".
     */
    onClick?: () => void;
    /**
     * Determines if the menu element is colored as selected. Defaults to `false` if not provided.
     */
    selected?: boolean;
}

export interface NavigationActionMenuElementBaseProps {
    onClick: () => void;
}

export interface NavigationLinkMenuElementBaseProps<LinkT extends React.ElementType> {
    Link: LinkT;
    LinkProps?: ComponentPropsWithRef<LinkT>;
}

export interface NavigationMenuElementProps extends NavigationMenuElementBaseProps {
    /**
     * Determines if the `NavigationDrawer` is expanded which cause a tooltip over the icon and slightly different styling. Defaults to `true` if `undefined`.
     *
     * Note: leave `undefined` or set to `true` if used within `burgerableMenu`
     */
    drawerExpanded?: boolean;
    /**
     * SVG-Icon to be displayed on the left side of a menu element if provided.
     */
    Icon: React.ElementType<SvgIconProps>;
}

export type NavigationActionMenuElementProps = NavigationMenuElementProps & NavigationActionMenuElementBaseProps;
// Note: With interface the property completion within `LinkProps` is working
export interface NavigationLinkMenuElementProps<LinkT extends React.ElementType> extends NavigationMenuElementProps, NavigationLinkMenuElementBaseProps<LinkT> {}

/**
 * Note that three properties are omitted because they are not valid for this special use case (and creating a new type with duplicated documentation feels also wrong:
 * - `drawerExpanded` is not needed because `NavigationExpandableMenuElement` cannot be shown in `NavigationDrawer` if collapsed and therefore only used in `BurgerableMenu`
 * - `onClick` is replaced by the more descriptive name `onToggleExpanded`. Also no need to call `onBurgerMenuClose` on this because it rather should collapse / expand
 * - `selected` is not needed because menu element just works as a "group" which itself has no action or link to be called
 */
export interface NavigationExpandableMenuElementProps extends Omit<NavigationMenuElementProps, "drawerExpanded" | "onClick" | "selected"> {
    expanded: boolean;
    onToggleExpanded: () => void;
    subElements: NavigationMenuSubElement[];
}

export type NavigationActionMenuSubElement = NavigationMenuElementBaseProps & NavigationActionMenuElementBaseProps;
export type NavigationLinkMenuSubElement<LinkT extends React.ElementType> = NavigationMenuElementBaseProps & NavigationLinkMenuElementBaseProps<LinkT>;
export type NavigationMenuSubElement = NavigationActionMenuSubElement | NavigationLinkMenuSubElement<React.ElementType>;

export const NavigationActionMenuElement: PlainFunctionComponent<NavigationActionMenuElementProps> = ({ onClick, badgeCounter, drawerExpanded, Icon, label, selected, testId }) => (
    <NavigationListItem drawerExpanded={drawerExpanded} onClick={onClick} selected={selected} testId={testId}>
        <NavigationListItemContent badgeCounter={badgeCounter} drawerExpanded={drawerExpanded} Icon={Icon} label={label} selected={selected} />
    </NavigationListItem>
);

export function NavigationLinkMenuElement<LinkT extends React.ElementType>({
    badgeCounter,
    drawerExpanded,
    Icon,
    Link,
    LinkProps,
    label,
    onClick,
    selected,
    testId,
}: NavigationLinkMenuElementProps<LinkT>): React.ReactElement<NavigationLinkMenuElementProps<LinkT>, string> {
    return (
        <NavigationListItem drawerExpanded={drawerExpanded} component={Link} onClick={onClick} selected={selected} testId={testId} {...LinkProps}>
            <NavigationListItemContent badgeCounter={badgeCounter} drawerExpanded={drawerExpanded} Icon={Icon} label={label} selected={selected} />
        </NavigationListItem>
    );
}

export const NavigationExpandableMenuElement: PlainFunctionComponent<NavigationExpandableMenuElementProps> = ({
    badgeCounter,
    expanded,
    Icon,
    label,
    onToggleExpanded,
    subElements,
    testId,
}) => {
    const classes = useStyles();
    return (
        <>
            <NavigationListItem onClick={onToggleExpanded} testId={testId}>
                <NavigationListItemContent badgeCounter={badgeCounter} Icon={Icon} label={label} />
                {expanded ? <ExpandLess key="collapse" /> : <ExpandMore key="expand" />}
            </NavigationListItem>
            <Collapse in={expanded} timeout="auto">
                <List component="nav" disablePadding={true}>
                    {subElements.map(({ badgeCounter, label, onClick, selected, testId, ...props }: NavigationMenuSubElement) => {
                        const additionalProps = "Link" in props ? { component: props.Link, ...props.LinkProps } : {};
                        return (
                            <NavigationListItem key={label.toString()} className={classes.subListItem} onClick={onClick} selected={selected} testId={testId} {...additionalProps}>
                                <NavigationListItemContent
                                    badgeCounter={badgeCounter}
                                    badgeClassName={clsx(classes.subListItemText, { [classes.subListItemTextSelected]: selected })}
                                    drawerExpanded={true}
                                    label={label}
                                    selected={selected}
                                />
                            </NavigationListItem>
                        );
                    })}
                </List>
            </Collapse>
        </>
    );
};

const useStyles = makeStyles(
    createStyles({
        subListItem: {
            padding: "10px 24px 10px 72px", // padding 10px because of missing Icon
        },
        subListItemText: {
            color: Color.blue1,
            fontWeight: "normal",
        },
        subListItemTextSelected: {
            color: Color.grey5,
        },
    }),
);
